import React, { Component } from 'react';
import { GamePlayerStatus } from '../../../../Constants';
import { getTradelaneCards, getTradelaneCardsForPlayer } from '../../Common/CardUtilities';

import './Actions/action-scroller.css';

import '../../Controls/animated-tradelanes.css';
import './inventory-selector.css';
import '../../Controls/orbits-2d.css';
import TheRiverItem from './TheRiverItem';
import '../../Controls/tradelane-selector.css';
import { HOTEL_BOARDS_URL } from '../../../../Settings';
import FireIcon from '../../Common/FireIcon';
import { getCardTextureUrl } from '../../Common/TextureTools';

const SCORING_ID = 4;
const BURNING_ID = 18;
const SLIDING_ID = 19;
// Manual 7.x: the four outermost (last-position) orbits burn at round
// start. Same set TheRiverCard / TradelaneSelector gate their will-burn
// glyph on.
const BURN_LANES = [ 4, 5, 10, 11 ];


// The TheRiverIndicator overlays (matching lodging / bed / amenity,
// available docks, free-advertisement) are a pure function of these
// gamePlayerData fields. Signature them so shouldComponentUpdate can
// force a (non-animated) re-render the instant any of them change —
// staffing a Bed or Amenity (station), gaining a Lodging Permit
// (lodging), or docking a guest (docks) must repaint the river even
// when the orbits array is unchanged.
function playerIndicatorSignature(gamePlayerData) {
    if (!gamePlayerData) return null;
    return JSON.stringify({
        id: gamePlayerData.id,
        station: gamePlayerData.station,
        lodging: gamePlayerData.lodging,
        docks: gamePlayerData.docks,
        robot_assignments: gamePlayerData.robot_assignments,
        turn_status: gamePlayerData.turn_status,
    });
}


// Manual 7.2.5 in-flight referral indicator (tradelane-in-flight) is a pure
// function of EVERY seat's referral state, not just the seated player's: a
// referral lands on another seat (status -> SPECIAL, special_card_orbit set),
// and that seat's row never touches the seated player's gamePlayerData. Sign
// the whole gameplayer_set so shouldComponentUpdate repaints the river the
// moment any seat enters or leaves a referral.
function referralSignature(gameData) {
    if (!gameData || !gameData.gameplayer_set) return '';
    return gameData.gameplayer_set
        .map(gp => `${gp.id}:${gp.status}:${gp.special_card_orbit ?? ''}`)
        .join(',');
}


// Orbits currently mid-referral: any seat in SPECIAL status carries the
// referred card's orbit in special_card_orbit (the card stays on the river
// during the referral window, Manual 7.2.5).
function referralOrbitIndices(gameData) {
    return new Set(
        (gameData && gameData.gameplayer_set ? gameData.gameplayer_set : [])
            .filter(gp => gp.status === GamePlayerStatus.SPECIAL_ID
                       && gp.special_card_orbit !== null
                       && gp.special_card_orbit !== undefined)
            .map(gp => gp.special_card_orbit));
}


function OrbitsBackground(props) {
    let src = HOTEL_BOARDS_URL;
    src += 'TradeLanes1v007.png';
    return <div id={'tradelanes-background'}
                className={'tradelanes-background'}>
        <img alt={'tradelanes-background'} src={src}
             className={'tradelanes-background-image'}
            // width={'100%'}
        />
    </div>
}


// Transient burn theater, decoupled from river truth. A copy of the
// departed card's texture rendered OVER its lane, spinning out under a
// fire flare, self-removing on its container's animationEnd. The lane
// underneath already shows current truth — the ghost is pure overlay,
// pointer-events: none so it never eats a click meant for the live card.
// The animation lives on the container; onAnimationEnd is bubbled-event
// guarded (target === currentTarget) so the child fire/image animations
// don't tear it down early.
function BurnGhost(props) {
    return <div className={'tradelane-burn-ghost'}
                onAnimationEnd={(e) => {
                    if (e.target !== e.currentTarget) return;
                    props.onDone();
                }}>
        <img className={'tradelane-burn-ghost-image'}
             src={getCardTextureUrl(props.cardId)}
             alt={'burning card'}/>
        <FireIcon className={'tradelane-burn-ghost-fire'}/>
    </div>;
}



export default class TheRiver extends Component {
    TRADELANE_INDEXES = [ 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11 ];
    REVERSE_TRADELANE_INDEXES = [11, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1, 0];

    constructor(props) {
        super(props);
        // if (props.bind) {
        //     this.props.app.gameController.gamePlayerOrbits.theRiver = this;
        // }
        this.state = {
            reRenderCount: 0,
            animate: false,
            // Burn ghosts keyed by tradelane index — one transient overlay
            // per lane. A new ghost at a lane REPLACES the prior one (keyed
            // render via burnGhostCounter), so at AUTO speed they flicker
            // rather than accumulate.
            burnGhosts: {},
        }

        // Set the instant a BURNING (18) update arrives, read+cleared when
        // the next state is consumed. Survives coalescing: even if the
        // BURNING orbits are overwritten by the SLIDING (19) state before
        // they ever render, this flag still tells nextUpdate to look for
        // departed burn-lane cards. Without it, an ad-removal from a burn
        // lane (dock/referral/discard) would be indistinguishable from a
        // real burn and spuriously spawn a ghost.
        this.sawBurning = false;
        // Monotonic so a replacement ghost at the same lane gets a fresh
        // React key and remounts (restarts its animation).
        this.burnGhostCounter = 0;


        // this.seat = this.props.gamePlayerData.seat;

        this.updateTimerLength = 3000; // modify this based on game speed.
        this.updateTimer = null;
        // Latest-state-wins (CC_RIVER_AUTO_PACE_COALESCE): a single pending
        // slot, NOT an unbounded FIFO. A new arrival OVERWRITES the pending
        // state so intermediate orbit states are never replayed in slow
        // motion behind the AI SpeedController. The dice-modal precedent.
        this.pendingUpdate = null;
        this.inProgress = false;

        this.tradelaneItems = {};

        // Pre-bound, one per lane, so React sees the same callback identity
        // every render and skips the detach (mutation) / reattach (layout)
        // cycle. Inline arrow refs churned: a synchronous dispatcher in a
        // JSX-earlier sibling (Dashboard mounts the AC at fragment position 0)
        // fires its CDU during the layout phase BEFORE this subtree reaches
        // layout, so this.tradelaneItems[idx] was null at dispatch time and
        // every highlight was silently skipped by the CC_AC_SINGLETON
        // `if (!item) return;` guard. Stable refs close that window.
        this.setTradelaneItemRef = {};
        this.TRADELANE_INDEXES.forEach((tradelaneIndex) => {
            this.setTradelaneItemRef[tradelaneIndex] = (ref) => {
                this.tradelaneItems[tradelaneIndex] = ref;
            };
        });

        this.allowUpdate = true;

        this.callback = null;

        this.currentTradelanesString = '';
        this.current = {
            tradelanes: this.props.gameData.orbits,
            status: this.props.gameData.status};

        this.mostRecentUpdateRequestString = '';

    }

    shouldComponentUpdate(nextProps, nextState, nextContext) {
        this.updateRequest(nextProps);
        // Orbits movement is handled by the animation queue above
        // (this.allowUpdate). Player-state overlays are not: repaint
        // immediately, non-animated, when the indicator-relevant
        // gamePlayerData fields change (Bed/Amenity staffing -> station,
        // Lodging Permit -> lodging, docking -> docks). Subsumes the
        // absent->present case (id is in the signature).
        const prevSig = playerIndicatorSignature(this.props.gamePlayerData);
        const nextSig = playerIndicatorSignature(nextProps.gamePlayerData);
        if (prevSig !== nextSig) {
            this.allowUpdate = true;
            return true;
        }
        // Cross-seat referral visibility: repaint when ANY seat's referral
        // state changes (status / special_card_orbit), even when the seated
        // player's own indicator fields are unchanged.
        const prevReferral = referralSignature(this.props.gameData);
        const nextReferral = referralSignature(nextProps.gameData);
        if (prevReferral !== nextReferral) {
            this.allowUpdate = true;
            return true;
        }
        return this.allowUpdate;
    }


    componentDidMount() {
        clearTimeout(this.updateTimer);
        this.nextUpdate();
        if (this.props.bind) {
            window.addEventListener('highlight-tradelanes-event',
                this.highlightTradelanesEvent);
            window.addEventListener('clear-tradelanes-highlight-event',
                this.clearTradelanesEvent);
            window.addEventListener('highlight-discard-event',
                this.highlightDiscardEvent);
            window.addEventListener('clear-discard-event',
                this.clearDiscardEvent);
            window.addEventListener('tradelanes-update-event',
                this.updateEvent);
            // Announce readiness so any highlight dispatcher that
            // mounted before this river (e.g. AdvertisementPossibilities
            // in the separate Desktop "Your Hotel" window, which renders
            // earlier than the standalone Orbital River window) can
            // re-send its tradelane highlight. Mirrors HotelInterface's
            // 'hotel-interface-mounted-event' handshake for docks —
            // without it the one-shot highlight races the river's mount
            // and is lost.
            window.dispatchEvent(new Event('river-mounted-event'));
        }
    }

    componentWillUnmount() {
        clearTimeout(this.updateTimer);
        window.removeEventListener('highlight-tradelanes-event',
            this.highlightTradelanesEvent);
        window.removeEventListener('clear-tradelanes-highlight-event',
            this.clearTradelanesEvent);
        window.removeEventListener('highlight-discard-event',
            this.highlightDiscardEvent);
        window.removeEventListener('clear-discard-event',
            this.clearDiscardEvent);
        window.removeEventListener('tradelanes-update-event',
            this.updateEvent);
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        clearTimeout(this.updateTimer);
        if (this.state.animate) {
            this.updateTimer = setTimeout(
                this.nextUpdate, this.updateTimerLength);
        } else {
            this.nextUpdate();
        }
    }

    updateRequest = (nextProps) => {
        const newTradelanes = nextProps.gameData.orbits;
        const newStatus = nextProps.gameData.status;
        const newData = {tradelanes: newTradelanes, status: newStatus};
        const newString = JSON.stringify(newTradelanes);
        if (this.mostRecentUpdateRequestString !== newString) {
            this.queueUpdate(newData);
            this.mostRecentUpdateRequestString = newString;
        }
    }

    updateEvent = (e) => {
        const newTradelanes = e.detail.tradelanes;
        const newStatus = e.detail.status;
        const newData = {tradelanes: newTradelanes, status: newStatus};
        const newString = JSON.stringify(newTradelanes);
        if (this.mostRecentUpdateRequestString !== newString) {
            this.queueUpdate(newData);
            this.mostRecentUpdateRequestString = newString;
        }
    }

    queueUpdate = (update) => {
        // Latch that a burn passed through THIS coalescing window. The
        // engine emits BURNING (18) then SLIDING (19) milliseconds apart
        // every round at any speed; the BURNING orbits are frequently
        // overwritten before they render, so the burn signal has to be
        // data (this flag), not which status happened to survive to a
        // render. Read+cleared in nextUpdate.
        if (update.status === BURNING_ID) {
            this.sawBurning = true;
        }
        // Coalesce: overwrite any pending state — intermediate states are
        // never replayed (latest-state-wins).
        this.pendingUpdate = update;
        if (this.inProgress) {
            // An update arrived while an animated frame is holding its
            // 3000ms timer (inProgress is true only between an animated
            // render and the timer firing). Cut the hold and process now so
            // the user sees current truth, not theater first (rule 3). Per
            // the gate in nextUpdate, cutting a hold renders non-animated.
            clearTimeout(this.updateTimer);
            this.nextUpdate(true);
        } else {
            this.nextUpdate(false);
        }
    }

    nextUpdate = (cuttingHold = false) => {
        this.inProgress = false;
        if (!this.pendingUpdate) {
            return;
        }
        let nextUpdate = this.pendingUpdate;
        this.pendingUpdate = null;
        // Read+clear the burn latch for this consumption. Captured before
        // the dedupe early-return so a stale flag can't leak into a later,
        // unrelated update.
        const sawBurning = this.sawBurning;
        this.sawBurning = false;
        if (this.currentTradelanesString === JSON.stringify(nextUpdate)) {
            // Already showing this exact state — dedupe no-op (preserved
            // from the FIFO implementation); nothing newer is pending.
            return;
        }
        // Burn-ghost detection runs BEFORE this.current is overwritten, off
        // the data (sawBurning), not the surviving status. A burn-lane card
        // present in the old orbits whose id no longer appears ANYWHERE in
        // the new orbits departed via the Burn Deck → spawn a ghost. The
        // "anywhere" test excludes cards that merely shifted position.
        let newGhosts = null;
        if (sawBurning) {
            newGhosts = this.detectBurnGhosts(
                this.current.tradelanes, nextUpdate.tradelanes);
        }
        this.current = nextUpdate;
        this.allowUpdate = true;
        // Latest-state-wins gate (CC_RIVER_AUTO_PACE_COALESCE). A consumed
        // state animates ONLY when it is the most recent known state — the
        // pending slot is empty after consumption — and we are not cutting
        // an in-flight hold to show current truth now (rule 3).
        const isLatest = this.pendingUpdate === null;
        let animate = isLatest && !cuttingHold;
        // BURNING (18, spin-out) and SLIDING (19, slide-in) are the
        // choreographed transitions. Pre-coalesce they force-animated even
        // with a backlog queued behind them — exactly what replayed stale
        // history in slow motion. The force-animate now survives ONLY under
        // this same gate: never against a non-empty pending slot, never
        // while cutting a hold.
        if (nextUpdate.status === BURNING_ID
                || nextUpdate.status === SLIDING_ID) {
            animate = isLatest && !cuttingHold;
        }
        // Ghosts ride the SAME render the pacing logic already scheduled —
        // they never set animate, never schedule updateTimer, never delay
        // nextUpdate. A new ghost at a lane that already has one replaces
        // it (bounded population at AUTO speed).
        const newState = {
            reRenderCount: ++this.state.reRenderCount,
            animate: animate,
        };
        if (newGhosts && Object.keys(newGhosts).length > 0) {
            newState.burnGhosts = { ...this.state.burnGhosts, ...newGhosts };
        }
        this.setState(newState);
    }


    detectBurnGhosts = (oldTradelanes, newTradelanes) => {
        const ghosts = {};
        if (!Array.isArray(oldTradelanes) || !Array.isArray(newTradelanes)) {
            return ghosts;
        }
        BURN_LANES.forEach((tradelaneIndex) => {
            const oldId = oldTradelanes[tradelaneIndex];
            if (oldId !== null && oldId !== undefined
                    && !newTradelanes.includes(oldId)) {
                this.burnGhostCounter += 1;
                ghosts[tradelaneIndex] = {
                    key: this.burnGhostCounter,
                    cardId: oldId,
                };
            }
        });
        return ghosts;
    }

    removeBurnGhost = (tradelaneIndex, key) => {
        const existing = this.state.burnGhosts[tradelaneIndex];
        // A replacement ghost (newer key) may have landed before this
        // animationEnd fired — don't let the stale one's removal evict the
        // live one.
        if (!existing || existing.key !== key) {
            return;
        }
        const burnGhosts = { ...this.state.burnGhosts };
        delete burnGhosts[tradelaneIndex];
        // shouldComponentUpdate gates on allowUpdate (false after every
        // render); without this the removal setState would be skipped and
        // the spent ghost would never leave the DOM. We deliberately do NOT
        // touch animate here — flipping it mid-slide would abort an
        // in-flight slide-in on the live lanes.
        this.allowUpdate = true;
        this.setState({
            reRenderCount: ++this.state.reRenderCount,
            burnGhosts: burnGhosts,
        });
    }


    highlightTradelanesEvent = (e) => {
        this.highlightOrbits(e.detail.tradelanes,
                             e.detail.callback)
    }

    clearTradelanesEvent = (e) => {
        this.clearSpecialIndicators();
    }

    highlightOrbits = (tradelanesToHighlight, callback) => {
        tradelanesToHighlight.forEach((tradelaneIndex) => {
            const item = this.tradelaneItems[tradelaneIndex];
            // Guard: a lane ref can be momentarily absent if the river
            // hasn't committed the item for that orbit yet. Without this
            // a single missing ref threw and killed the whole batch.
            if (!item) return;
            item.highlight();
        })
        this.callback = callback;
    };

    clearSpecialIndicators = () => {
        this.TRADELANE_INDEXES.forEach((tradelaneIndex) => {
            let item = this.tradelaneItems[tradelaneIndex];
            if (!item) return;
            item.clearIndicators();
        });
    };

    refreshIndicators = () => {
        this.setState({
            reRenderCount: ++this.state.reRenderCount,
            animate: false,
        });
    };

    highlightDiscardEvent = (e) => {
        this.showDiscard(e.detail.tradelane, e.detail.callback);
    }

    clearDiscardEvent = (e) => {
        this.clearSpecialIndicators();
    }

    showDiscard = (tradelaneIndex, callback) => {
        this.callback = callback;
        let indicator = this.tradelaneItems[tradelaneIndex].indicatorComponent;
        indicator.showDiscard();
    };

    onClick = (tradelaneIndex) => {
        console.log('orbitClick', tradelaneIndex);
        let indicator = this.tradelaneItems[tradelaneIndex].indicatorComponent;
        if (indicator.state.discard || indicator.state.highlight) {
            indicator.clearIndicators();
            this.callback(tradelaneIndex);
        // }
        // else if (indicator.state.free) {
        //     let correctTurn = this.props.gamePlayerData.turn_status === 5;
        //     if (correctTurn) {
        //         // this is free advertisement
        //         this.props.app.gameController.routeLocalChoice(
        //             32, {tradelane: tradelaneIndex});
        //     } else {
        //         console.log('Lock First')
        //     }
        } else {
            console.log('wasted click');
        }
    };

    renderBurnGhost = (tradelaneIndex) => {
        const ghost = this.state.burnGhosts[tradelaneIndex];
        if (!ghost) {
            return null;
        }
        return <BurnGhost key={`burn-ghost-${ghost.key}`}
                          cardId={ghost.cardId}
                          onDone={() => this.removeBurnGhost(
                              tradelaneIndex, ghost.key)}/>;
    }

    render() {
        if (!this.allowUpdate) {
            console.error('Render while not allowed.')
        }
        this.allowUpdate = false;
        this.inProgress = true;
        const newTradelanes = this.current.tradelanes;
        const newStatus = this.current.status;
        this.currentTradelanesString = JSON.stringify({tradelanes: newTradelanes, status: newStatus});
        // When every orbit is empty, the river takes zero real estate.
        // Covers all three mount points — Dashboard standalone panel,
        // GamePlayer2d's nested copy, Game2d's observer-mode copy —
        // because each falls back to its parent's flex/grid layout
        // when this component returns null.
        const allOrbitsEmpty = Array.isArray(newTradelanes)
            && newTradelanes.every((cardId) => !cardId);
        if (!Array.isArray(newTradelanes) || allOrbitsEmpty) {
            return null;
        }
        let gamePlayerData = null;
        if (this.props.gamePlayerData) {
            gamePlayerData = this.props.gamePlayerData;
        }
        let tradelanesData;
        if (gamePlayerData) {
            tradelanesData = getTradelaneCardsForPlayer(
                { orbits: newTradelanes },
                gamePlayerData)
        } else {
            tradelanesData = getTradelaneCards(
                newTradelanes,
                gamePlayerData)
        }

        let bottomRow = [ 0, 1, 2, 3, 4, 5 ];
        let topRow = [ 6, 7, 8, 9, 10, 11 ];

        // Manual 7.2.5: orbits whose card is mid-referral get the atypical
        // in-flight border so every seat sees the card in transit.
        const referralOrbits = referralOrbitIndices(this.props.gameData);

        return (<>
            <div className={'tradelane-selector'}>
                <OrbitsBackground {...this.props} />
                {topRow.map((tradelaneIndex) => {
                    return <div className={'tradelane-selector-item'} key={`tl-${tradelaneIndex}`}>
                        <TheRiverItem tradelaneIndex={tradelaneIndex}
                                      onClick={this.onClick}
                                      ref={this.setTradelaneItemRef[tradelaneIndex]}
                                      status={newStatus}
                                      tradelaneData={tradelanesData[tradelaneIndex]}
                                      inFlight={referralOrbits.has(tradelaneIndex)}
                                      animate={this.state.animate}
                        />
                        {this.renderBurnGhost(tradelaneIndex)}
                    </div>;
                })}
                {bottomRow.map((tradelaneIndex) => {
                    return <div className={'tradelane-selector-item'} key={`tl-${tradelaneIndex}`}>
                        <TheRiverItem tradelaneIndex={tradelaneIndex}
                                      onClick={this.onClick}
                                      ref={this.setTradelaneItemRef[tradelaneIndex]}
                                      status={newStatus}
                                      tradelaneData={tradelanesData[tradelaneIndex]}
                                      inFlight={referralOrbits.has(tradelaneIndex)}
                                      animate={this.state.animate}
                        />
                        {this.renderBurnGhost(tradelaneIndex)}
                    </div>;
                })}
            </div>
        </>);
    }
}
